<template>
    <app-base-main parentTable>
      <template #filter-group>
        <app-filter-group
            @query="$gjNoticeQuery"
            @reset="$gjReset">
          <app-filter-item>
            <gj-select-input
                v-model="filter.code"
                :options="option.code"
                :width="['100px', '120px']"
            ></gj-select-input>
          </app-filter-item>
          <app-filter-item>
            <gj-select
                v-model="filter.statusList"
                :options="option.status"
                multiple
                collapse-tags
                placeholder="状态"
                width="150px"
            ></gj-select>
          </app-filter-item>
          <app-filter-item priority="2">
            <gj-supplier-select
                v-model="filter.supplierCode"
                placeholder="供应商"
            ></gj-supplier-select>
          </app-filter-item>
          <app-filter-item>
            <gj-warehouse-select
                v-model="filter.warehouseId"
                placeholder="采购仓"
                :fba="false"
                width="150px"
            ></gj-warehouse-select>
          </app-filter-item>
          <app-filter-item>
            <gj-select-input
                v-model="filter.keyword"
                :options="option.keyword"
                :width="['100px', '100px']"
            ></gj-select-input>
          </app-filter-item>
          <app-filter-item priority="4">
            <gj-creator-select
                v-model="filter.creator"
                placeholder="创建人"
            ></gj-creator-select>
          </app-filter-item>
          <app-filter-item priority="3">
            <gj-creator-select
                v-model="filter.purchaser"
                placeholder="采购员"
            ></gj-creator-select>
          </app-filter-item>
          <app-filter-item priority="1">
            <gj-select-date-picker
                v-model="filter.date"
                :options="option.date"
                type="daterange"
            ></gj-select-date-picker>
          </app-filter-item>


          <template #right>
            <view-setting
                    :ref="REF_VIEW_SETTING"
                    save-mode="remote"
                    :use-default="true"
                    :module-name="moduleName"
                    :config="model"
                    :fn-generate-params="generateViewParams"
                    @view-change="handleChangeView"
                    @view-complete="handCompleteView"
            >
            </view-setting>
          </template>
        </app-filter-group>
      </template>
      <template #operate-group>
        <gj-button
            v-if="$permission('purchaseOrder:add')"
            type="primary"
            @click="add">
          添加
        </gj-button>
        <template v-if="$permission('purchaseOrder:audit')">
          <gj-button
              :disabled="noSelect || selectedSomeNotIn('status', [StatusEnum.REJECTED, StatusEnum.DRAFT])"
              @click="audit(selectedRows)">
            提审
          </gj-button>
          <gj-button
              :disabled="noSelect || selectedSomeIsNot('status', StatusEnum.WAIT_REVIEW)"
              @click="auditPass(selectedRows)">
            审核通过
          </gj-button>
          <gj-button
              :disabled="noSelect || selectedSomeIsNot('status', StatusEnum.WAIT_REVIEW)"
              @click="auditFail(selectedRows)">
            审核驳回
          </gj-button>
        </template>
        <gj-button
            v-if="$permission('purchaseOrder:add')"
            :disabled="noSelect || selectedSomeIsNot('status', StatusEnum.WAIT_ORDER)"
            @click="purchase(selectedRows)">
          下单
        </gj-button>
        <gj-button
            v-if="$permission('purchaseOrder:endDeliveryNote')"
            :disabled="noSelect || selectedSomeIsNot('status', StatusEnum.WAIT_ARRIVE) || !sameSupplierCode"
            @click="endDelivery(selectedRows)">
          结束交货
        </gj-button>
      </template>
      <template #tool-group>
        <gj-tool-group
            :download="$permission('purchaseOrder:export')" download-url="/supply/purchaseOrder/export" :download-params="globalParams"
            help
        ></gj-tool-group>
      </template>
      <template #table>
        <gj-table
            ref="gjTable"
            adaptParent
            :name="tableName"
            :loading="loading"
            :data="tableData"
            :columns="columns"
            :scroll-x="{gt: -1}"
            :scroll-y="{gt: -1}"
            selection
            :show-overflow="false"
            :bak-selected-rows="bakSelectedRows"
            :bak-rows="selectedRows"
            @clearBakSelectedRows="clearBakRows"
            :selectable="(row) => row.$gjParentData && !row.$gjExtendRow"
            :extend-row-config="{prop: 'extendRow'}"
            :structure-config="{type: 'ParentChild', children: 'products', parentSpan: [1, 9]}"
            sortable="custom"
            :pagination="pagination"
            :default-sort="{prop: 'createTime', order: 'descending'}"
            @sort-change="$gjSortChange"
            @page-change="$gjNoticeQuery">

          <template v-slot:picture="{row}">
            <template v-if="row.$gjParentData">
              <div style="display: inline-block; width: 150px;">
                {{row.code | defaultShow}}
                <gj-copy-icon :content="row.code"></gj-copy-icon>
              </div>
              <div v-gutter="20" style="display: inline-block;">
                <span>{{row.deliveryWarehouseName | defaultShow}}</span>
                <gj-tag
                    type="plain"
                    shape="rectangle"
                    :color="row.status | optionColor(PurchaseOrderEnum.statusOption)">
                  {{ row.status | optionText(PurchaseOrderEnum.statusOption) }}
                </gj-tag>
                <gj-text max-width="100px">{{row.supplierName | defaultShow}}</gj-text>
                <span><span class="parent-table-label">采购员：</span>{{row.supervisor | defaultShow}}</span>
                <span><span class="parent-table-label">总金额：</span>{{row.totalAmount | amount | defaultShow(row.currencySymbol)}}</span>
                <span><span class="parent-table-label">创建人：</span>{{row.createUserName | defaultShow}}</span>
                <span><span class="parent-table-label">创建时间：</span>{{row.createTime | defaultShow}}</span>
                <span><span class="parent-table-label">采购量/交货量/入库量：</span>{{row.totalOrderQuantity || 0}} / {{row.totalAlreadyDeliveryQuantity || 0}} / {{row.totalInboundQuantity || 0}}</span>
                <span><span class="parent-table-label">备注：</span><gj-text max-width="200px">{{row.memo | defaultShow}}</gj-text></span>
              </div>
            </template>
            <template v-else>
              <gj-image :src="row.smallImageUrl"></gj-image>
            </template>
          </template>

          <template v-slot:sku="{row}">
            <div>
              <gj-text>{{row.productName | defaultShow}}</gj-text>
            </div>
            <div>{{row.sku | defaultShow}}</div>
            <div>{{row.productType | optionText(productTypeOption)}} - {{row.categoryName | defaultShow}}</div>
          </template>
          <template v-slot:purchasePlanCode="{row}">
           <template v-if="!row.$gjParentData">
             <template v-if="row.purchasePlanCodeList && row.purchasePlanCodeList.length !== 0">
               <el-dropdown>
                 <span class="el-dropdown-link" style="font-size: 12px">
                    <span>{{row.purchasePlanCodeList[0] | defaultShow}} <i class="el-icon-arrow-down el-icon--right" v-if="row.purchasePlanCodeList.length > 1"></i></span>
                  </span>
                 <el-dropdown-menu slot="dropdown">
                   <template v-if="row.purchasePlanCodeList.length > 1">
                     <template v-for="(item,key) in row.purchasePlanCodeList" >
                       <el-dropdown-item :key="key"><span style="font-size: 12px">{{ item }}</span></el-dropdown-item>
                     </template>
                   </template>
                 </el-dropdown-menu>
               </el-dropdown>
             </template>
             <template v-else>
               -
             </template>
           </template>
          </template>
          <template v-slot:op="{ row }">
            <template v-if="row.$gjParentData">
              <gj-button-group
                  type="dropdown-op"
                  :config="opBtnConfig(row)"
              ></gj-button-group>
            </template>
          </template>
          <template v-slot:extend:row="{row}">
            <div class="default">
              <div v-if="!row.expand">
                <gj-link type="default" @click="openDetail(row,true)">
                  更多（共{{row.num}}条）
                  <i class="iconfont iconzhankai"></i>
                </gj-link>
              </div>
              <div v-else>
                <gj-link type="default" @click="openDetail(row, false)">
                  收起
                  <i class="iconfont iconshouqi"></i>
                </gj-link>
              </div>
            </div>
          </template>
        </gj-table>
      </template>
    </app-base-main>
</template>

<script>
  import {queryMixin, responseMixin, selectionMixin, valueCheckMixin, bakSelectRowsMixin} from "@/minxins";
  import {PurchaseOrderEnum} from "./enum";
  import {
    auditFailPurchaseOrder,
    auditPassPurchaseOrder,
    auditPurchaseOrder, cancelPurchaseOrder, endDeliveryPurchaseOrder,
    getPurchaseOrderPage, purchasePurchaseOrder
  } from "@/api/purchase";
  import AppOpPurchaseOrderModal from "./modal/op/op-purchase-order-modal";
  import {OpEnum, ProductEnum} from "@/utils/options";
  import AppCreateDeliveryOrderModal from "./modal/delivery/create-delivery-order-modal";
  import AppFastInboundModal from "./modal/inbound/fast-inbound-modal";
  import AppPurchaseChangeModal from "./modal/change/purchase-change-modal";
  import {defaultVal, isMeaningful, unique} from "@/utils/common";
  import {convertToDate, generateViewParams} from "@/utils/date.util";
  import {exportFile} from "@/api/common";
  import model from './viewConfig'

  const StatusEnum = PurchaseOrderEnum.status;

  export default {
    name: "app-purchase-order",
    mixins: [queryMixin, selectionMixin, responseMixin, valueCheckMixin, bakSelectRowsMixin],
    data() {
      return {
        tableName: 'purchaseOrder',
        tableData: [],
        columns: [
          {id: 'picture', header: '图片', field: 'slot:picture', width: '50px'},
          {id: 'sku', header: '产品名称/SKU/产品类型-品类', field: 'slot:sku', minWidth: '300px'},
          {id: 'orderQuantity', header: '采购量', field: 'orderQuantity', width: '80px', align: 'right'},
          {id: 'includeTaxPrice', header: '含税单价', field: 'includeTaxPrice', prefix: 'currencySymbol', width: '120px', align: 'right'},
          {id: 'amount', header: '金额', field: 'amount', prefix: 'currencySymbol', width: '120px', align: 'right'},
          {id: 'memo', header: '备注', field: 'memo', width: '200px'},
          {id: 'alreadyDeliveryQuantity', header: '交货量', field: 'alreadyDeliveryQuantity', width: '150px', align: 'right'},
          {id: 'inboundQuantity', header: '入库量', field: 'inboundQuantity', width: '180px', align: 'right'},
          {id: 'expectDeliveryDate', header: '预计交货日期', field: 'expectDeliveryDate', width: '100px'},
          {id: 'purchasePlanCode', header: '采购计划编号', field: 'slot:purchasePlanCode', width: '180px'},
          {id: 'op', header: '操作', field: 'slot:op', width: '160px', notCustomColumn: true, fixed: 'right'},
        ],
        opBtnConfig: (row) => [
          {
            label: '详情',
            click: () => this.view({...row, products: row.productsCopy})
          },
          {
            label: '编辑',
            show: () => this.$permission('purchaseOrder:add')
              && this.someIs(row.status, [StatusEnum.DRAFT, StatusEnum.REJECTED]),
            click: () => this.edit({...row, products: row.productsCopy})
          },
          {
            label: '下单',
            show: () => this.$permission('purchaseOrder:add')
              && row.status === StatusEnum.WAIT_ORDER,
            click: () => this.purchase([{...row, products: row.productsCopy}])
          },
          {
            label: '创建交货单',
            show: () => this.$permission('purchaseOrder:addDeliveryNote')
              && row.status === StatusEnum.WAIT_ARRIVE
              && row.totalOrderQuantity > row.totalAlreadyDeliveryQuantity,
            click: () => this.createDeliveryOrder([{...row, products: row.productsCopy}])
          },
          {
            label: '快捷入库',
            show: () => this.$permission('purchaseOrder:fastInbound')
              && row.status === StatusEnum.WAIT_ARRIVE
              && row.totalOrderQuantity > row.totalAlreadyDeliveryQuantity,
            click: () => this.fastInbound([{...row, products: row.productsCopy}])
          },
          {
            label: '采购变更',
            show: () => this.$permission('purchaseOrder:createPurchaseChangeOrder')
              && this.someIs(row.status, [StatusEnum.WAIT_ORDER, StatusEnum.WAIT_ARRIVE, StatusEnum.COMPLETED]),
            click: () => this.purchaseChange({...row, products: row.productsCopy})
          },
          {
            label: '结束交货',
            show: () => this.$permission('purchaseOrder:endDeliveryNote')
              && row.status === StatusEnum.WAIT_ARRIVE,
            click: () => this.endDelivery([{...row, products: row.productsCopy}])
          },
          {
            label: '提审',
            show: () => this.$permission('purchaseOrder:add')
              && this.someIs(row.status, [StatusEnum.DRAFT, StatusEnum.REJECTED]),
            click: () => this.audit([{...row, products: row.productsCopy}])
          },
          {
            label: '审核通过',
            show: () => this.$permission('purchaseOrder:audit')
              && row.status === StatusEnum.WAIT_REVIEW,
            click: () => this.auditPass([{...row, products: row.productsCopy}])
          },
          {
            label: '审核驳回',
            show: () => this.$permission('purchaseOrder:audit')
              && row.status === StatusEnum.WAIT_REVIEW,
            click: () => this.auditFail([{...row, products: row.productsCopy}])
          },
          {
            label: '导出合同',
            show: () => this.$permission('purchaseOrder:exportContract')
              && this.someIs(row.status, [StatusEnum.WAIT_REVIEW, StatusEnum.WAIT_ORDER, StatusEnum.WAIT_ARRIVE, StatusEnum.COMPLETED]),
            click: () => this.exportContract({...row, products: row.productsCopy})
          },
          {
            label: '作废',
            show: () => this.$permission('purchaseOrder:abolish')
              && this.someIs(row.status, [StatusEnum.DRAFT, StatusEnum.WAIT_REVIEW, StatusEnum.REJECTED, StatusEnum.WAIT_ORDER, StatusEnum.WAIT_ARRIVE]),
            click: () => this.cancel([{...row, products: row.productsCopy}])
          },
          {
            label: '日志',
            click: () => this.viewLog({...row, products: row.productsCopy})
          },
        ],
        filter: {
          date: {
            key: 'createDate',
            value: null
          }
        },
        option: {
          code: [
            {id: 'code', text: '采购单号'},
            {id: 'purchasePlanCode', text: '采购计划'},
          ],
          status: PurchaseOrderEnum.statusOption,
          supplier: [],
          keyword: [
            {id: 'sku', text: 'SKU'},
            {id: 'productName', text: '产品名称'},
          ],
          date: [
            {id: 'createDate', text: '创建时间'},
            {id: 'expectDeliveryDate', text: '预计交货时间'},
          ]
        },
        PurchaseOrderEnum: PurchaseOrderEnum,
        StatusEnum: StatusEnum,
        productTypeOption: ProductEnum.typeOption,
        moduleName: 'PURCHASE_ORDER',
        model,
      }
    },
    computed: {
      sameSupplierCode() {
        return unique(this.selectedRows, 'supplierCode').length === 1;
      },
    },
    created(){
      this.sort.prop = 'createTime';
    },
    methods: {
      generateViewParams,
      /**
       * 设置全局查询参数
       */
      mergeRemoteParams (params) {
        const date = params.date ? params.date : { key: params.dateType, value: [params.startDateTime, params.endDateTime]}
        this.filter = Object.assign({}, this.filter, {
          ...params,
          date
        })
      },
      loadViewComplete () {
        this.$emit('query')
      },
      $gjQueryInitParams() {
        return {
          filter: {
            code: {
              key: 'code',
              value: null
            },
            statusList: null,
            supplierCode: null,
            warehouseId: '',
            keyword: {
              key: 'sku',
              value: null
            },
            creator: null,
            purchaser: null,
            date: {
              key: 'createDate',
              value: null
            }
          },
        };
      },
      $gjQuery() {
        // 构造查询参数
        const params = {
          model: {
            statusList: this.filter.statusList,
            supplierCode: this.filter.supplierCode,
            deliveryWarehouseId: this.filter.warehouseId,
            [this.filter.keyword.key]: this.filter.keyword.value,
            [this.filter.code.key]: this.filter.code.value,
            createUserId: defaultVal(this.filter.creator),
            supervisorId: defaultVal(this.filter.purchaser),
          },
          ...this.queryPagination,
          ...this.querySort
        };
        if (isMeaningful(this.filter.date.value)) {
          if (this.filter.date.key === 'createDate') {
            params.model.startCreateTime = convertToDate(this.filter.date.value[0]);
            params.model.endCreateTime = convertToDate(this.filter.date.value[1]);
          } else if (this.filter.date.key === 'expectDeliveryDate') {
            params.model.startExpectDeliveryDate = convertToDate(this.filter.date.value[0]);
            params.model.endExpectDeliveryDate = convertToDate(this.filter.date.value[1]);
          }
        }
        this.globalParams = params;
        // 查询
        this.loading = true;
        getPurchaseOrderPage(params)
          .then((data) => {
            this.tableData = data.records.map((v, i) => {
              const products = this._.cloneDeep(v.products) || [];
              return Object.assign(v, {
                productsCopy: products,
                products: products.length > 3 ? products.slice(0, 3) : products,
                expand: false,
                dataIndex: i
              })
            });
            this.pagination = this.getPagination(data);
          })
          .complete(() => {
            this.loading = false;
          });
      },
      add() {
        this.$gjCreate(AppOpPurchaseOrderModal, {
          props: {
            header: `添加`,
            opType: OpEnum.type.ADD
          },
          on: {
            created: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      edit(row) {
        this.$gjCreate(AppOpPurchaseOrderModal, {
          props: {
            header: row.code,
            opType: OpEnum.type.UPDATE,
            purchaseOrder: {
              ...row,
              products: row.productsCopy
            }
          },
          on: {
            updated: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      view(row) {
        this.$gjCreate(AppOpPurchaseOrderModal, {
          props: {
            header: row.code,
            opType: OpEnum.type.VIEW,
            purchaseOrder: {
              ...row,
              products: row.productsCopy
            }
          },
          on: {
            resetPage: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      createDeliveryOrder(rows) {
        this.$gjCreate(AppCreateDeliveryOrderModal, {
          props: {
            purchaseOrders: rows,
          },
          on: {
            created: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      fastInbound(rows) {
        this.$gjCreate(AppFastInboundModal, {
          props: {
            purchaseOrders: rows
          },
          on: {
            created: () => {
              this.$gjResetPage();
            }
          }
        });
      },
      purchaseChange(row) {
        this.$gjCreate(AppPurchaseChangeModal, {
          props: {
            purchaseOrder: row
          },
          on: {
            created: () => {
              this.$gjResetPage();
            }
          }
        })
      },
      purchase(rows) {
        const modal = this.$gjCreate("gj-info-modal", {
          props: {
            title: '下单确认',
            content: rows.length === 1
              ? `确定要下单[${rows[0].code}]吗？该采购订单状态更新为“待交货”！`
              : `已选中 ${rows.length} 个采购订单，确定要批量下单吗？该采购订单状态更新为“待交货”！`
          },
          on: {
            cancel: () => modal.$gjDestroy(),
            confirm: () => {
              modal.confirmLoading = true;
              purchasePurchaseOrder({
                idList: rows.map(e => e.id),
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          },
        });
      },
      endDelivery(rows) {
        const modal = this.$gjCreate("gj-info-modal", {
          props: {
            title: '结束交货确认',
            content: rows.length === 1
              ? `确定要结束交货[${rows[0].code}]吗？`
              : `已选中 ${rows.length} 个采购订单，确定要结束交货吗？`,
          },
          on: {
            cancel: () => modal.$gjDestroy(),
            confirm: () => {
              modal.confirmLoading = true;
              endDeliveryPurchaseOrder({
                idList: rows.map(e => e.id),
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          },
        });
      },
      audit(rows) {
        const modal = this.$gjCreate("gj-info-modal", {
          props: {
            title: '提审确认',
            content: rows.length === 1
              ? `确定要提审[${rows[0].code}]吗？`
              : `已选中 ${rows.length} 个采购订单，确定要批量提审吗？`,
            contentStyle: {'color': '#333', 'font-size': '12px'}
          },
          on: {
            cancel: () => modal.$gjDestroy(),
            confirm: () => {
              modal.confirmLoading = true;
              auditPurchaseOrder({
                idList: rows.map(e => e.id),
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          },
        });
      },
      auditPass(rows) {
        const modal = this.$gjCreate("gj-info-modal", {
          props: {
            title: '通过确认',
            content: rows.length === 1
              ? `确定要通过[${rows[0].code}]吗？`
              : `已选中 ${rows.length} 个采购订单，确定要批量通过吗？`,
            contentStyle: {'color': '#333', 'font-size': '12px'}
          },
          on: {
            cancel: () => modal.$gjDestroy(),
            confirm: () => {
              modal.confirmLoading = true;
              auditPassPurchaseOrder({
                idList: rows.map(e => e.id),
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          },
        });
      },
      auditFail(rows) {
        const modal = this.$gjCreate('gj-textarea-modal', {
          props: {
            header: rows.length > 1 ? '审核驳回' : `审核驳回<span class="modal-secondary-title">${rows[0].code}</span>`,
            label: '驳回理由',
            labelWidth: '74px',
            contentStyle: {padding: '16px 20px 0 16px'},
          },
          on: {
            submit: (reason) => {
              modal.confirmLoading = true;
              auditFailPurchaseOrder({
                idList: rows.map(e => e.id),
                reason,
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          },
        });
      },
      exportContract(row) {
        exportFile(`/supply/purchaseOrder/export/contract/${row.id}`);
      },
      cancel(rows) {
        const modal = this.$gjCreate('gj-textarea-modal', {
          props: {
            header: rows.length > 1 ? '作废' : `作废<span class="modal-secondary-title">${rows[0].code}</span>`,
            label: '作废理由',
            labelWidth: '74px',
            contentStyle: {padding: '16px 20px 0 16px'}
          },
          on: {
            submit: (memo) => {
              modal.confirmLoading = true;
              cancelPurchaseOrder({
                id: rows[0].id,
                memo,
              })
                .then(res => {
                  this.$gjResponseHandle(res, () => {
                    this.$gjMessage.success('操作成功');
                    this.$gjResetPage();
                    modal.$gjDestroy();
                  });
                })
                .complete(() => modal.confirmLoading = false);
            }
          }
        });
      },
      viewLog(row) {
        this.$gjCreate('gj-log-modal', {
          props: {
            url: '/supply/purchaseOrder/get/log/page',
            params: {
              model: row.id
            },
            columns: PurchaseOrderEnum.logColumns
          }
        });
      },
      openDetail(row, flag){
        if (flag) {
          row.products = row.productsCopy;
          row.expand = flag;
        } else {
          row.products = row.products.slice(0,3);
          row.expand = flag;
        }
        this.tableData = this.tableData.map(v => {
          if (v.id === row.id) {
            v.expand = row.expand
            v.products = row.products
          }
          return v
        })
        this.bakSelectedRows = this._.cloneDeep(this.selectedRows);
      }
    }
  }
</script>

<style scoped lang="scss">
.iconfont{
  font-size: 10px;
  color: #999;
}
.iconshouqi{
  margin-left: 5px;
}
.default {
  /deep/ .gj-link{
    &:hover{
      .iconfont{
        color: #1F6EEF;
      }
    }
  }
}
</style>

